import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Calendar, User, FileText, TrendingUp, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDisclaimer = () => {
  const navigate = useNavigate();
  const [acceptances, setAcceptances] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [limit] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    fetchData();
  }, [page]);

  useEffect(() => {
    if (searchTerm.trim()) {
      handleSearch();
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  }, [searchTerm]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch acceptances
      const acceptancesRes = await axios.get(
        `${API}/admin/disclaimer-audit?skip=${page * limit}&limit=${limit}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAcceptances(acceptancesRes.data.acceptances || []);
      
      // Fetch stats
      const statsRes = await axios.get(
        `${API}/admin/disclaimer-audit/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(statsRes.data);
      
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const token = localStorage.getItem('token');
      
      // Search in all acceptances
      const response = await axios.get(
        `${API}/admin/disclaimer-audit/search?query=${encodeURIComponent(searchTerm)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSearchResults(response.data.results || []);
      
      if (response.data.results.length === 0) {
        toast.info('Keine Ergebnisse gefunden');
      } else {
        toast.success(`${response.data.results.length} Ergebnis(se) gefunden`);
      }
    } catch (error) {
      toast.error('Fehler bei der Suche');
      console.error(error);
      setSearchResults([]);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setIsSearching(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const displayData = isSearching ? searchResults : acceptances;

  if (loading && !stats) {
    return (
      <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
        <div className="flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p style={{ color: '#E6EDF3' }}>Laden...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold mb-8" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
          Disclaimer Audit-Log
        </h2>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm" style={{ color: '#9CA3AF' }}>Gesamt Sessions</p>
                    <p className="text-2xl font-bold" style={{ color: '#E6EDF3' }}>
                      {stats.total_sessions}
                    </p>
                  </div>
                  <FileText className="w-8 h-8" style={{ color: '#7CD4FD' }} />
                </div>
              </CardContent>
            </Card>

            <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm" style={{ color: '#9CA3AF' }}>Akzeptiert</p>
                    <p className="text-2xl font-bold" style={{ color: '#10B981' }}>
                      {stats.disclaimer_accepted}
                    </p>
                  </div>
                  <CheckCircle className="w-8 h-8" style={{ color: '#10B981' }} />
                </div>
              </CardContent>
            </Card>

            <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm" style={{ color: '#9CA3AF' }}>Nicht akzeptiert</p>
                    <p className="text-2xl font-bold" style={{ color: '#EF4444' }}>
                      {stats.disclaimer_not_accepted}
                    </p>
                  </div>
                  <Calendar className="w-8 h-8" style={{ color: '#EF4444' }} />
                </div>
              </CardContent>
            </Card>

            <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm" style={{ color: '#9CA3AF' }}>Akzeptanzrate</p>
                    <p className="text-2xl font-bold" style={{ color: '#7CD4FD' }}>
                      {stats.acceptance_rate}%
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8" style={{ color: '#7CD4FD' }} />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Search Bar */}
        <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-2" style={{ color: '#9CA3AF' }}>
                  Suche nach E-Mail, Name oder Session-ID
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="z.B. max@example.com oder Max Mustermann"
                  className="w-full px-4 py-2 rounded-lg"
                  style={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    color: '#E6EDF3'
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
              </div>
              <div className="flex gap-2 items-end">
                <Button
                  onClick={handleSearch}
                  disabled={!searchTerm.trim()}
                  style={{
                    backgroundColor: searchTerm.trim() ? '#7CD4FD' : '#374151',
                    color: searchTerm.trim() ? '#0B0F14' : '#6B7280'
                  }}
                >
                  Suchen
                </Button>
                {isSearching && (
                  <Button
                    onClick={clearSearch}
                    style={{
                      backgroundColor: '#EF4444',
                      color: '#FFFFFF'
                    }}
                  >
                    Zurücksetzen
                  </Button>
                )}
              </div>
            </div>
            
            {isSearching && (
              <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: '#1F2937' }}>
                <p style={{ color: '#7CD4FD', fontSize: '14px' }}>
                  🔍 Suchfilter aktiv: "{searchTerm}" - {searchResults.length} Ergebnis(se)
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Acceptances Table */}
        <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle style={{ color: '#E6EDF3' }}>
              {isSearching ? 'Suchergebnisse' : 'Alle Disclaimer-Akzeptierungen'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: '1px solid #1F2937' }}>
                    <th className="text-left p-3" style={{ color: '#9CA3AF' }}>Session ID</th>
                    <th className="text-left p-3" style={{ color: '#9CA3AF' }}>Benutzer</th>
                    <th className="text-left p-3" style={{ color: '#9CA3AF' }}>E-Mail</th>
                    <th className="text-left p-3" style={{ color: '#9CA3AF' }}>Akzeptiert am</th>
                    <th className="text-left p-3" style={{ color: '#9CA3AF' }}>Session erstellt</th>
                  </tr>
                </thead>
                <tbody>
                  {displayData.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center p-8" style={{ color: '#9CA3AF' }}>
                        {isSearching ? 'Keine Ergebnisse gefunden' : 'Keine Daten verfügbar'}
                      </td>
                    </tr>
                  ) : (
                    displayData.map((acceptance) => (
                      <tr 
                        key={acceptance.id} 
                        style={{ borderBottom: '1px solid #1F2937' }}
                        className="hover:bg-gray-800 transition-colors"
                      >
                        <td className="p-3">
                          <button
                            onClick={() => navigate(`/report/${acceptance.id}`)}
                            className="flex items-center gap-2 hover:underline transition-all"
                            style={{ color: '#7CD4FD' }}
                            title="Diagnose anzeigen"
                          >
                            <code className="text-xs">
                              {acceptance.id.substring(0, 8)}...
                            </code>
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        </td>
                        <td className="p-3" style={{ color: '#E6EDF3' }}>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" style={{ color: '#7CD4FD' }} />
                            {acceptance.user_name || 'N/A'}
                          </div>
                        </td>
                        <td className="p-3" style={{ color: '#9CA3AF' }}>
                          {acceptance.user_email || 'N/A'}
                        </td>
                        <td className="p-3" style={{ color: '#10B981' }}>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            {formatDate(acceptance.disclaimer_accepted_at)}
                          </div>
                        </td>
                        <td className="p-3" style={{ color: '#9CA3AF' }}>
                          {formatDate(acceptance.created_at)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination - only show when not searching */}
            {!isSearching && acceptances.length > 0 && (
              <div className="flex justify-between items-center mt-6 pt-4" style={{ borderTop: '1px solid #1F2937' }}>
                <Button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  style={{
                    backgroundColor: page === 0 ? '#1F2937' : '#7CD4FD',
                    color: page === 0 ? '#6B7280' : '#0B0F14'
                  }}
                >
                  Vorherige
                </Button>
                
                <span style={{ color: '#9CA3AF' }}>
                  Seite {page + 1}
                </span>
                
                <Button
                  onClick={() => setPage(page + 1)}
                  disabled={acceptances.length < limit}
                  style={{
                    backgroundColor: acceptances.length < limit ? '#1F2937' : '#7CD4FD',
                    color: acceptances.length < limit ? '#6B7280' : '#0B0F14'
                  }}
                >
                  Nächste
                </Button>
              </div>
            )}
            
            {/* Search results count */}
            {isSearching && searchResults.length > 0 && (
              <div className="mt-6 pt-4 text-center" style={{ borderTop: '1px solid #1F2937' }}>
                <span style={{ color: '#9CA3AF' }}>
                  {searchResults.length} Ergebnis(se) gefunden
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Box */}
        <Card className="mt-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', borderLeft: '4px solid #7CD4FD' }}>
          <CardContent className="p-6">
            <h3 className="font-semibold mb-2" style={{ color: '#7CD4FD' }}>
              ℹ️ Über diesen Audit-Log
            </h3>
            <p style={{ color: '#9CA3AF', fontSize: '14px', lineHeight: '1.6' }}>
              Dieser Audit-Log zeigt alle Disclaimer-Akzeptierungen mit Zeitstempel. 
              Dies ist wichtig für rechtliche Compliance (DSGVO) und Haftungsschutz. 
              Jede Diagnose-Session erfordert eine separate Zustimmung, die hier dokumentiert wird.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
